/**
 * https://alphagroup.larkenterprise.com/wiki/WRRBwvSsAiJRv1kBUNJcSLGGnxb?sheet=cYvqXe
 */
import mixpanel, { type Dict } from 'mixpanel-browser';
import { detectPlatform } from '@/utils/device';

interface ClickWatchData {
  event_source?: string;
  video_type: string;
  exclusive_video: boolean;
  video_title: string;
  source_title: string;
}

/** Allowed video_type values for video_played (Mixpanel spec) */
const VIDEO_TYPES = ['live match', 'live event', 'moments', 'movie', 'episode', 'clip'] as const;
type VideoType = (typeof VIDEO_TYPES)[number];

/** Allowed event_source values for video_played (Mixpanel spec) */
const EVENT_SOURCES = [
  'match_page',
  'search_page',
  'club_page',
  'league_page',
  'home_page',
  'moments',
  'push_notification',
  'exclusive_video',
] as const;
type EventSource = (typeof EVENT_SOURCES)[number];

interface VideoPlayedData {
  video_id: string;
  video_type: string;
  video_title: string;
  event_source: string;
  exclusive_video: string; // "true" | "false" per spec
  session_id: string;
  start_minute: string;
  end_minute: string;
  audio_settings: string; // "commentator" | "stadium" | "" - never "balanced"
  video_quality: string;
  commentator_name: string;
  wifi: boolean;
  teams_names: string;
  league_name: string;
  show_name: string;
  live: boolean;
  total_video_duration: string;
  single_match?: boolean; // iOS/Android only - multi-view
  muted: boolean;
  duration_watched: string;
}

interface CommentatorChangedData {
  previous_commentator: string;
  new_commentator: string;
  match_id: string;
  match_title: string;
}

interface SubscriptionPageOpenData {
  locked_content_type: string;
  locked_content_title: string;
}

interface ConcurrentSessionsLimitPopupData {
  session_id: string;
  video_title: string;
  video_type: string;
  wall_type: 'absolute_limit' | 'subscription_redirection';
  user_choice: string;
  subscription_status: string;
  subscription_plan: string;
  platform?: string;
  UTM_source?: string;
  UTM_medium?: string;
}

mixpanel.init('d1419941bb5b25424acba97c36cb0ab0', {
  debug: process.env?.NODE_ENV === 'development' ? true : false,
});

/**
 * 更新 mixpanel People Property
 * @param key key
 * @param val value
 */
export function mixpanelPeopleSet<T>(key: string, val: T) {
  mixpanel.people.set({
    [key]: val,
  });
}
/**
 * 更新 mixpanel People Increment
 * @param {string|Dict} prop 属性
 */
export function mixpanelPeopleIncrement(prop: string | Dict) {
  mixpanel.people.increment(prop);
}

export function track(event: string, properties?: Record<string, any>) {
  try {
    const enrichedProperties = {
      ...properties,
      ...(!properties?.platform && { platform: detectPlatform() }),
    };
    console.log('mixpanel track', event, enrichedProperties);
    mixpanel?.track(event, enrichedProperties);
  } catch (error) {
    console.error('track error', error);
  }
}

/**
 * 点击观看时上报
 * @param data 点击观看时上报的参数
 */
export function clickWatch(data: ClickWatchData) {
  try {
    track('click_watch', {
      event_source: data.event_source || 'home_page', // 来源
      video_type: data.video_type, // 服务端下发的视频类型
      exclusive_video: data.exclusive_video, // 是否为独家类型（付费内容）
      video_title: data.video_title,
      source_title: data.source_title,
    });
  } catch (error) {
    console.error('clickWatch error', error);
  }
}
/**
 * Normalize CMS video_type to allowed Mixpanel values.
 * Allowed: live match, live event, moments, movie, episode, clip
 */
export function normalizeVideoType(raw: string | undefined, isLive: boolean, matchType?: number): string {
  if (isLive) {
    return matchType === 1 ? 'live event' : 'live match';
  }
  const lower = (raw || '').toLowerCase().trim();
  const mapping: Record<string, VideoType> = {
    'live match': 'live match',
    'live event': 'live event',
    live_match: 'live match',
    live_event: 'live event',
    moments: 'moments',
    movie: 'movie',
    episode: 'episode',
    clip: 'clip',
    highlights: 'clip',
    goals: 'clip',
    summary: 'clip',
    reels: 'moments',
    video: 'clip',
  };
  return mapping[lower] || (lower ? 'clip' : '');
}

/**
 * Normalize event_source to allowed Mixpanel values.
 * Allowed: match_page, search_page, club_page, league_page, home_page, moments, push_notification, exclusive_video
 */
export function normalizeEventSource(raw: string | undefined): string {
  const lower = (raw || '').toLowerCase().replace(/\s+/g, '_').trim();
  const mapping: Record<string, EventSource> = {
    match_page: 'match_page',
    search_page: 'search_page',
    search: 'search_page',
    club_page: 'club_page',
    club: 'club_page',
    league_page: 'league_page',
    league: 'league_page',
    home_page: 'home_page',
    home: 'home_page',
    moments: 'moments',
    push_notification: 'push_notification',
    exclusive_video: 'exclusive_video',
  };
  return mapping[lower] || 'home_page';
}

/**
 * video_played event - Triggered after user plays at least 3s of a video.
 *
 * IMPORTANT: clip_played is DEPRECATED and must NOT be sent anywhere in the platform.
 * All video/clip play events use video_played with the correct video_type (e.g. "clip").
 * @param data Video playback analytics data
 */
export function videoPlayed(data: VideoPlayedData) {
  try {
    track('video_played', {
      video_id: data.video_id,
      video_type: data.video_type,
      video_title: data.video_title,
      event_source: data.event_source,
      exclusive_video: data.exclusive_video,
      session_id: data.session_id,
      start_minute: data.start_minute,
      end_minute: data.end_minute,
      audio_settings: data.audio_settings,
      video_quality: data.video_quality,
      commentator_name: data.commentator_name,
      wifi: data.wifi,
      teams_names: data.teams_names,
      league_name: data.league_name,
      show_name: data.show_name,
      live: data.live,
      total_video_duration: data.total_video_duration,
      ...(data.single_match !== undefined && { single_match: data.single_match }),
      muted: data.muted,
      duration_watched: data.duration_watched,
    });
  } catch (error) {
    console.error('videoPlayed error', error);
  }
}

/**
 * 解说员切换时上报
 * @param data 解说员切换时上报的参数
 */
export function commentatorChanged(data: CommentatorChangedData) {
  try {
    track('commentator_changed', {
      previous_commentator: data.previous_commentator, // 之前的解说员
      new_commentator: data.new_commentator, // 解说员名字
      match_id: data.match_id, // 比赛id
      match_title: data.match_title, // 比赛标题
    });
  } catch (error) {
    console.error('commentatorChanged error', error);
  }
}
export function contentConsumptionMilestone(data: {
  consumption_percent: number; // 到达关键百分比节点 （如果是比赛直播应该按110分钟为100%, 25, 50, 75, 100
  video_id: string; // 视频id
  video_type: string; // 视频类型
  video_title: string; // 视频标题
  event_source: string; // 来源
  exclusive_video: boolean; // 是否为独家类型（付费内容）
  source_title: string; // 来源标题
  video_quality: string; // 视频质量
  commentator_name: string; // 解说员名字
  wifi: boolean;
  teams_names: string; // 队伍名称
  league_name: string; // 联赛名称
  live: boolean; // 是否直播
  total_video_duration: string; // 视频总时长
  duration_watched: string; // 已观看时长
  end_minute: string; // 视频需要
  watched_to_end: boolean; // 是否观看到最后
}) {
  try {
    track('content_consumption_milestone', {
      consumption_percent: data.consumption_percent, // 到达关键百分比节点 （如果是比赛直播应该按110分钟为100%, 25, 50, 75, 100
      video_id: data.video_id, // 视频id
      video_type: data.video_type, // 视频类型
      video_title: data.video_title, // 视频标题
      event_source: data.event_source, // 来源
      exclusive_video: data.exclusive_video, // 是否为独家类型（付费内容）
      source_title: data.source_title, // 来源标题
      video_quality: data.video_quality, // 视频质量
      commentator_name: data.commentator_name, // 解说员名字
      wifi: true,
      teams_names: data.teams_names, // 队伍名称
      league_name: data.league_name, // 联赛名称
      live: data.live, // 是否直播
      total_video_duration: data.total_video_duration, // 视频总时长
      duration_watched: data.duration_watched, // 已观看时长
      end_minute: data.end_minute, // 视频需要
      watched_to_end: data.watched_to_end, // 是否观看到最后
    });
  } catch (error) {
    console.error('contentConsumptionMilestone error', error);
  }
}

/**
 * 设置页打开时上报
 */
export function settingPageOpen() {
  track('settings_open');
}

/**
 * 首页打开时上报
 */
export function homePageOpen() {
  track('home_page_open', {
    tab_selected: 'sport', // 首页选中的tab
  });
}

export function subscriptionPageOpen(data: SubscriptionPageOpenData) {
  try {
    track('subscription_sheet_view', {
      event_source: 'home_page', // 来源
      locked_content_type: data.locked_content_type,
      locked_content_title: data.locked_content_title,
    });
  } catch (error) {
    console.error('subscriptionPageOpen error', error);
  }
}

export function signPageOpen() {
  try {
    track('sign_in_screen_viewed', {
      event_source: 'onboarding', // 来源
    });
  } catch (error) {
    console.error('signPageOpen error', error);
  }
}
export function signupMethod(method?: string) {
  try {
    if (!method) {
      console.warn('signupMethod skipped: missing method');
      return;
    }
    const eventName = 'tv_scan_qrcode_login';
    track(eventName, {
      signup_method: method,
      platform: detectPlatform(),
    });
  } catch (error) {
    console.error('[Analytics] signupMethod error:', error);
  }
}

export function concurrentSessionsLimitPopupShown(data: ConcurrentSessionsLimitPopupData) {
  try {
    track('concurrent_sessions_limit_popup_shown', {
      session_id: data.session_id,
      video_title: data.video_title,
      video_type: data.video_type,
      wall_type: data.wall_type,
      user_choice: data.user_choice,
      subscription_status: data.subscription_status,
      subscription_plan: data.subscription_plan,
      platform: data.platform || detectPlatform(),
      UTM_source: data.UTM_source || '',
      UTM_medium: data.UTM_medium || '',
    });
  } catch (error) {
    console.error('concurrentSessionsLimitPopupShown error', error);
  }
}

let sessionSpendTimer: ReturnType<typeof setTimeout>;
let sessionBeforeUnloadHandler: any;
/**
 * 页面停留10s上报，未达到时不上报
 */
export function sessionSpend() {
  try {
    if (sessionBeforeUnloadHandler) {
      window.removeEventListener('beforeunload', sessionBeforeUnloadHandler);
    }
    window.addEventListener(
      'beforeunload',
      (sessionBeforeUnloadHandler = () => {
        clearTimeout(sessionSpendTimer);
      }),
    );
    clearTimeout(sessionSpendTimer);
    sessionSpendTimer = setTimeout(() => {
      track('session', {
        session_source: 'direct', // 来源
      });
    }, 10 * 1000);
  } catch (error) {
    console.error('sessionSpend error', error);
  }
  window.addEventListener(
    'beforeunload',
    (sessionBeforeUnloadHandler = () => {
      clearTimeout(sessionSpendTimer);
    }),
  );
  clearTimeout(sessionSpendTimer);
  sessionSpendTimer = setTimeout(() => {
    track('session', {
      session_source: 'direct', // 来源
      event_source: 'direct', // 来源
    });
  }, 10 * 1000);
}
